/**
 * SSO 파트너 복귀 URL 힌트 (Task: sso-partner-return-url)
 * 저장: SSO 로그인 성공 직후(SsoConfirmView) — 항상 overwrite (유효값 set / 무효·null 은 clear).
 * 사용: 강제 리다이렉트·로그아웃 시점만. 읽어도 삭제하지 않는다 — 로그아웃 후 뒤로가기로 앱에
 * 재진입해도 내부 signin 이 아니라 파트너로 다시 복귀시키기 위함(Task: sso-logout-email-display).
 * 삭제: 비-SSO access token 감지(token-store) + 저장된 값이 무효일 때.
 * localStorage 접근은 전부 try/catch, "호출 시점" 참조 (모듈 로드 시점 캡처 금지 — 테스트 스텁).
 * JWT payload 는 base64url — atob 단독 금지, decodeJwtPayload 패턴 사용 (B-2).
 * auth-redirect.ts 의 sessionStorage returnUrl(로그인 "후" 내부 복귀용)과 목적이 다름 — 통합 금지.
 */

import { routing } from '@/shared/i18n/routing';

const SSO_RETURN_URL_HINT_KEY = 'sso_partner_return_url';
// 바운스 카운터는 sessionStorage(탭 단위·풀 리로드에도 생존) — 모듈 변수는 문서마다 리셋된다.
const SSO_BOUNCE_KEY = 'sso_partner_return_bounce';
const MAX_URL_LENGTH = 512;
const PARTNER_REDIRECT_GUARD_MS = 15_000;
// 파트너가 앱으로 되돌려 보내는 구성에서 앱↔파트너 풀 리로드 루프가 생길 수 있다.
// 짧은 창 안에서 N회를 넘으면 힌트를 포기하고 내부 signin 으로 폴백해 루프를 끊는다.
const BOUNCE_WINDOW_MS = 60_000;
const MAX_BOUNCES_PER_WINDOW = 3;
/**
 * 루프를 끊었을 때 보낼 곳 — 내부 로그인 화면이 아니라 SSO 안내 화면이다.
 * SSO 로 들어온 사용자에게 내부 signin 을 보이지 않는 것이 이 힌트 기능의 목적이고,
 * /sso/error 는 flow 쿠키 없이도 접근 가능하며 "파트너에서 다시 시도" 안내를 이미 갖고 있다.
 * 로케일 프리픽스 필수: `app/sso/[slug]/callback` 때문에 `/sso/*` 는 로케일 리다이렉트를 타지
 * 않아 프리픽스 없는 `/sso/error` 는 404 다 (로컬 실측 2026-09-01).
 */
const SSO_LOOP_FALLBACK_PATH = '/sso/error';

/** 현재 경로의 로케일을 이어받아 하드 내비게이션용 절대 경로를 만든다 (없으면 defaultLocale). */
function buildSsoLoopFallbackPath(): string {
  const segment = window.location.pathname.split('/')[1] ?? '';
  const locale = (routing.locales as readonly string[]).includes(segment)
    ? segment
    : routing.defaultLocale;
  return `/${locale}${SSO_LOOP_FALLBACK_PATH}`;
}

interface SsoAccessTokenPayload {
  amr?: unknown;
}

// 이중 소비 레이스 가드 (라운드 1 B-1): AuthProvider 리다이렉트와 인터셉터 navigateToSignIn 은
// 상태를 공유하지 않고 같은 만료 이벤트에서 둘 다 발화할 수 있다 (AuthProvider tokenRefresh 는
// 인터셉터 비경유 throw + finally 후 account-me 쿼리 발화 → 401 → 인터셉터).
// 뒤따르는 signin assign 이 파트너 assign 을 덮지 못하게 "내비게이션 자체를 스킵"시킨다.
// 시한부(15s): assign 이 커밋되지 않는 예외 상황(beforeunload "머무르기" 등)에서
// 가드가 영구 고착돼 이후 signin 폴백까지 막는 것을 방지 (라운드 2 M-2 잔여·m-2).
let partnerRedirectStartedAt: number | null = null;

const isPartnerRedirectInFlight = (): boolean => {
  return (
    partnerRedirectStartedAt != null &&
    Date.now() - partnerRedirectStartedAt < PARTNER_REDIRECT_GUARD_MS
  );
};

// realtime-auth-refresh-orchestrator.ts:19-32 의 decodeJwtPayload 패턴 (base64url-안전).
// persistAccountId(token-store.ts) 는 atob 단독 호출로 이 버그를 공유하므로 재사용 금지.
const decodeJwtPayloadSafe = (token: string): SsoAccessTokenPayload | null => {
  try {
    const [, payload] = token.split('.');
    if (!payload || typeof globalThis.atob !== 'function') return null;

    const base64Payload = payload
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(Math.ceil(payload.length / 4) * 4, '=');
    return JSON.parse(globalThis.atob(base64Payload)) as SsoAccessTokenPayload;
  } catch {
    return null;
  }
};

/** 테스트 전용 export — 프로덕션 소비자는 resolveSsoRedirectDestination/redirect 의 검증 통과 값만 사용해야 한다. */
export function isValidSsoReturnUrl(value: unknown): value is string {
  if (
    typeof value !== 'string' ||
    value.length === 0 ||
    value.length > MAX_URL_LENGTH
  ) {
    return false;
  }

  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return false;
  }

  if (url.protocol !== 'https:') return false;
  if (url.username !== '' || url.password !== '') return false;
  if (url.hostname === '') return false;

  return true;
}

const clearSsoReturnUrlHint = (): void => {
  try {
    localStorage.removeItem(SSO_RETURN_URL_HINT_KEY);
  } catch {
    // 무시
  }
};

/** overwrite 시맨틱 (라운드 1 M-1) — 유효값 set / null·무효값은 clear. */
export function setSsoReturnUrlHint(url: string | null | undefined): void {
  if (typeof window === 'undefined' || window.desktop) return;

  const isNonEmptyString = typeof url === 'string' && url.length > 0;

  try {
    if (isValidSsoReturnUrl(url)) {
      localStorage.setItem(SSO_RETURN_URL_HINT_KEY, url);
      return;
    }

    if (isNonEmptyString) {
      console.warn('[sso-return-url-hint] 무효 URL 거부:', url);
    }
    localStorage.removeItem(SSO_RETURN_URL_HINT_KEY);
  } catch {
    // 무시
  }
}

interface BounceRecord {
  count: number;
  firstAt: number;
}

/**
 * 바운스 예산 차감 — 창(60s) 안에서 MAX 회를 넘으면 false(= 파트너 이동 포기).
 * 삭제 대신 카운터를 쓰는 이유: 힌트를 지우면 로그아웃 후 뒤로가기가 다시 signin 으로 떨어진다.
 * sessionStorage 가 없거나 throw 하면 예산 검사를 건너뛴다(기능 우선, 루프는 in-flight 가드에 의존).
 */
function consumeBounceBudget(): boolean {
  let store: Storage;
  try {
    store = sessionStorage;
    if (!store) return true;
  } catch {
    return true;
  }

  const now = Date.now();
  let record: BounceRecord = { count: 0, firstAt: now };
  try {
    const raw = store.getItem(SSO_BOUNCE_KEY);
    if (raw != null) {
      const parsed = JSON.parse(raw) as Partial<BounceRecord>;
      if (
        typeof parsed?.count === 'number' &&
        typeof parsed?.firstAt === 'number' &&
        now - parsed.firstAt < BOUNCE_WINDOW_MS
      ) {
        record = { count: parsed.count, firstAt: parsed.firstAt };
      }
    }
  } catch {
    // 파싱 실패 = 새 창으로 취급
  }

  if (record.count >= MAX_BOUNCES_PER_WINDOW) return false;

  try {
    store.setItem(
      SSO_BOUNCE_KEY,
      JSON.stringify({ count: record.count + 1, firstAt: record.firstAt }),
    );
  } catch {
    // 기록 실패해도 이동은 허용
  }
  return true;
}

/**
 * SSO 세션이었던 사용자를 어디로 보낼지 결정한다 (내비게이션은 호출자가 — use-logout 전용:
 * 정리 후 이동). 반환: 파트너 절대 URL / 루프 차단 시 SSO 안내 경로 / 힌트 없으면 null.
 * 힌트는 삭제하지 않는다: 로그아웃 후 파트너 페이지에서 뒤로가기로 앱에 재진입하면
 * AuthProvider 가 같은 힌트로 다시 파트너에 복귀시켜야 한다. 정리는 비-SSO 로그인 감지
 * (syncSsoReturnUrlHintWithToken)와 새 SSO 로그인 overwrite 가 담당한다.
 */
export function resolveSsoRedirectDestination(): string | null {
  if (typeof window === 'undefined' || window.desktop) return null;
  if (isPartnerRedirectInFlight()) return null;

  try {
    const raw = localStorage.getItem(SSO_RETURN_URL_HINT_KEY);
    if (raw == null) return null;

    if (!isValidSsoReturnUrl(raw)) {
      localStorage.removeItem(SSO_RETURN_URL_HINT_KEY); // 무효값은 남겨둘 이유가 없다
      return null;
    }

    partnerRedirectStartedAt = Date.now();
    // 루프 감지 시에도 내부 signin 으로 떨어뜨리지 않고 SSO 안내 화면으로 보낸다.
    return consumeBounceBudget() ? raw : buildSsoLoopFallbackPath();
  } catch {
    return null;
  }
}

/**
 * 힌트가 있으면 파트너(루프 차단 시 SSO 안내 화면)로 하드 내비게이션까지 수행하고 true
 * — true 시 호출자는 어떤 내비게이션도 금지
 */
export function redirectToPartnerIfHinted(): boolean {
  if (isPartnerRedirectInFlight()) return true; // 이미 파트너로 가는 중 — 호출자 내비게이션 금지

  const url = resolveSsoRedirectDestination();
  if (url == null) return false;

  window.location.assign(url);
  return true;
}

/** access token payload 의 amr 검사 — 비-SSO 세션이면 힌트 삭제 (token-store 에서 호출) */
export function syncSsoReturnUrlHintWithToken(accessToken: string): void {
  if (!accessToken) return; // setAccessToken('') 초기화(SignInForm.tsx:99)·로그아웃 no-op
  if (typeof window === 'undefined') return;

  const payload = decodeJwtPayloadSafe(accessToken);
  if (payload == null) return; // 판단 불가 → 유지

  const amr = payload.amr;
  if (!(Array.isArray(amr) && amr.includes('sso'))) {
    clearSsoReturnUrlHint();
  }
}

/** 테스트 전용 — 모듈 상태(inFlight 가드) 리셋. 바운스 카운터는 sessionStorage 스텁으로 리셋한다. */
export function __resetSsoReturnUrlHintGuardForTest(): void {
  partnerRedirectStartedAt = null;
}
